import {
    Box, Button,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    LinearProgress,
    Stack, styled,
    Switch,
    TextField, Typography
} from "@mui/material";
import axios from "axios";
import React, {useEffect, useRef, useState} from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import UploadIcon from "@mui/icons-material/Upload";

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const StyledCode = styled('code')({
    background: "#f6f7f8",
    padding: "2px 4px",
    fontSize: "0.8125rem",
    border: "1px solid #dfe2e7",
    borderRadius: "6px",
    marginBottom: "10px",
    marginTop: "2px",
    display: "inline-block"
});

export default function Settings() {
    const [data, setData] = useState({
        "servoDuration": "",
        "snapShotTimeOut": "",
        "servoStatus": null,
        "cameraStatus": null
    });
    const [loading, setLoading] = useState(false);

    const refCert = useRef<HTMLInputElement | null>(null);
    const refKey = useRef<HTMLInputElement | null>(null);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            await axios.post('/settings/save', {
                formData: data
            }).then((response) => {
                if (response?.data !== null && response?.data !== undefined) {
                    window.flash("Settings saved", 'success');
                }
            });
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setLoading(false);
    };

    const handleRestart = async () => {
        setLoading(true);
        try {
            await axios.get('/restart').then((response) => {
                if (response?.data !== null && response?.data !== undefined) {
                    window.flash("Restarting", 'success');
                }
            });
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setLoading(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = event.target;
        setData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const getSettings = async () => {
        setLoading(true);
        try {
            await axios.get('/settings').then((response) => {
                if (response?.data !== null && response?.data !== undefined) {
                    setData(response?.data)
                }
            });
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        getSettings();
    }, []);

    const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name} = event.target;
        if (event.target.checked) {
            setData((prevData) => ({
                ...prevData,
                [name]: 2,
            }));
        } else {
            setData((prevData) => ({
                ...prevData,
                [name]: 1,
            }));
        }
    };

    const handleCertChange = async () => {
        const selectedFile = refCert.current?.files && refCert.current?.files[0];
        if (!selectedFile) return

        if (selectedFile) {
            const reader = new FileReader();

            reader.readAsDataURL(selectedFile);
        }

        await cert(selectedFile)
    };

    const cert = async (selectedFile: File) => {
        const formData = new FormData();
        formData.append('file', selectedFile);
        setLoading(true);

        try {
            await axios.post('/uploadcert', formData, {
                headers: {
                    'type': 'cert'
                },
            }).then((response) => {
                if (response.data) {
                    window.flash("Uploaded Cert", 'success');
                }
            })
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setLoading(false);
    };

    const handleKeyChange = async () => {
        const selectedFile = refKey.current?.files && refKey.current?.files[0];
        if (!selectedFile) return

        if (selectedFile) {
            const reader = new FileReader();

            reader.readAsDataURL(selectedFile);
        }

        await key(selectedFile)
    };

    const key = async (selectedFile: File) => {
        const formData = new FormData();
        formData.append('file', selectedFile);
        setLoading(true);

        try {
            await axios.post('/uploadcert', formData, {
                headers: {
                    'type': 'key'
                },
            }).then((response) => {
                if (response.data) {
                    window.flash("Uploaded Key", 'success');
                }
            })
        } catch (error) {
            console.error(error);
            if (error && typeof error === 'object' && 'data' in error) {
                const errorMessage = (error as any).data?.message || 'An unknown error occurred';
                window.flash(errorMessage, 'error');
            }
        }
        setLoading(false);
    };

    return (
        <>
            {loading && <LinearProgress color="secondary"/>}

            <Box maxWidth={500} sx={{m: "auto"}} pt={0} pl={2} pr={2}>
                {data && (
                    <Stack spacing={2} mt={14}>
                        <TextField
                            disabled={loading}
                            name="servoDuration"
                            label="Servo Motor Duration"
                            variant="standard"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                            }}
                            value={data.servoDuration}
                            onChange={handleChange}
                        />

                        <TextField
                            disabled={loading}
                            name="snapShotTimeOut"
                            label="Camera Snapshot timeout"
                            variant="standard"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                            }}
                            value={data.snapShotTimeOut}
                            onChange={handleChange}
                        />

                        <FormGroup>
                            <FormControlLabel control={
                                <Switch
                                    disabled={loading}
                                    name="cameraStatus"
                                    checked={data.cameraStatus === 2}
                                    onChange={handleSwitch}
                                />
                            } label="AI Camera"/>

                            <FormControlLabel control={
                                <Switch
                                    disabled={loading}
                                    name="servoStatus"
                                    checked={data.servoStatus === 2}
                                    onChange={handleSwitch}
                                />
                            } label="Servo Motor"/>
                        </FormGroup>

                        <LoadingButton
                            loading={loading}
                            onClick={handleSubmit}
                            loadingPosition="start"
                            startIcon={<SaveIcon/>}
                            variant="contained"
                        >
                            Save Settings
                        </LoadingButton>


                        <Typography variant="h5" pt={10}>
                            SSL
                        </Typography>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography>
                                SSL Cert.der
                            </Typography>
                            <Button
                                disabled={loading}
                                component="label"
                                role={undefined}
                                onChange={handleCertChange}
                                variant="outlined"
                                tabIndex={-1}
                                startIcon={<UploadIcon/>}
                            >
                                Cert
                                <VisuallyHiddenInput ref={refCert} type="file"/>
                            </Button>
                        </Stack>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography>
                                SSL key.der
                            </Typography>
                            <Button
                                disabled={loading}
                                component="label"
                                role={undefined}
                                onChange={handleKeyChange}
                                variant="outlined"
                                tabIndex={-1}
                                startIcon={<UploadIcon/>}
                            >
                                Key
                                <VisuallyHiddenInput ref={refKey} type="file"/>
                            </Button>
                        </Stack>
                        <LoadingButton
                            loading={loading}
                            onClick={handleRestart}
                            loadingPosition="start"
                            startIcon={<SaveIcon/>}
                            variant="contained"
                        >
                            Restart
                        </LoadingButton>
                    </Stack>
                )}
            </Box>
            <Box maxWidth={500} sx={{m: "auto"}} pb={10} pl={2} pr={2}>
                <Typography mt={10}>
                    Create Certification
                </Typography>
                <StyledCode>sudo certbot certonly --dns-duckdns-token --token </StyledCode>
                <br/>

                <Typography mt={2}>
                    Download Certification & key
                </Typography>
                <StyledCode>sudo cp ../xx/fullchain.pem /Users/xx/Desktop</StyledCode>
                <br/>
                <StyledCode>sudo cp ../xx/privkey.pem /Users/xx/Desktop</StyledCode>

                <Typography mt={2}>
                    Convert to der
                </Typography>
                <StyledCode>openssl x509 -in fullchain.pem -outform der -out cert.der</StyledCode>
                <StyledCode>sudo openssl ec -in privkey.pem -outform der -out key.key.der </StyledCode>
            </Box>
        </>
    )
}
